import { UserRole } from '../types';

/** Werftools-medewerkers (zien normaal alle tenants, afhankelijk van RLS). */
export function isWerftoolsStaffRole(role: string | undefined | null): boolean {
    const r = String(role || '').toUpperCase();
    return r === UserRole.ADMIN || r === UserRole.RECRUITER;
}

/** Externe klantgebruikers — strikte tab- en datascope. */
export function isClientScopedRole(role: string | undefined | null): boolean {
    const r = String(role || '').toUpperCase();
    return r === UserRole.CLIENT || r === UserRole.CLIENT_RECRUITER;
}

/** Standaard menu voor Client Admin / Client Recruiter (toolklant / bureau). */
export const CLIENT_OPERATIONS_VISIBLE_TABS: string[] = [
    'dashboard',
    'pipeline',
    'shortlist',
    'workspace',
    'vacancies',
    'vacancy_templates',
    'talentpool',
    'placements',
    'reporting',
    'documentbuilder',
    'docs',
    'cv_templates',
    'doc_shortcodes',
    'engagement_center',
    'automation',
    'crm',
    'careers_site',
    'products',
    'users',
];

/** Ontbraken vroeger in Gebruikers → Module toegang; oude profielen misten ze nog. */
const LEGACY_CLIENT_OPS_TABS = ['pipeline', 'shortlist', 'crm', 'workspace', 'reporting', 'engagement_center', 'vacancy_templates'];

/**
 * Profielen met “alles aan” in de oude modulelijst kregen nooit pipeline/shortlist/crm
 * (stonden niet in UserManagement). Voeg die toe als de gebruiker al een volledig pakket heeft.
 */
export function expandLegacyClientVisibleTabs(tabs: string[]): string[] {
    if (!tabs.length) return tabs;
    const set = new Set(tabs);
    const looksLikeFullClientAccess =
        set.has('talentpool') &&
        set.has('vacancies') &&
        (set.has('workspace') || set.has('documentbuilder') || set.has('automation'));
    if (!looksLikeFullClientAccess) return tabs;
    for (const id of LEGACY_CLIENT_OPS_TABS) set.add(id);
    return [...set];
}

/**
 * Zichtbare tabs voor het menu.
 * - ADMIN / RECRUITER: lege array = volledig menu (Layout.tsx).
 * - CLIENT / CLIENT_RECRUITER: profiles.visible_tabs (+ legacy-uitbreiding).
 */
export function resolveVisibleTabsFromProfile(
    profile: { role?: string | null; visible_tabs?: string[] | null },
    opts?: { isClientPortal?: boolean },
): string[] {
    const role = profile.role || '';
    const fromDb = Array.isArray(profile.visible_tabs)
        ? profile.visible_tabs.filter((t): t is string => typeof t === 'string' && t.trim().length > 0)
        : [];

    if (isWerftoolsStaffRole(role)) {
        return [];
    }

    if (isClientScopedRole(role)) {
        if (fromDb.length > 0) {
            return expandLegacyClientVisibleTabs(fromDb);
        }
        if (opts?.isClientPortal) {
            return ['dashboard', 'vacancies', 'talentpool', 'engagement_center', 'pipeline', 'shortlist'];
        }
        return [...CLIENT_OPERATIONS_VISIBLE_TABS];
    }

    if (fromDb.length > 0) {
        return fromDb;
    }

    return ['dashboard', 'vacancies'];
}

type ClientOrgRow = { id: string; name?: string; parent_client_id?: string | null; client_type?: string };

/** Organisaties die een gebruiker mag kiezen bij uitnodigen/bewerken (geen sibling toolklanten). */
export function filterOrganizationsForActor(
    allClients: ClientOrgRow[],
    actor: { role?: string | null; clientId?: string | null },
): ClientOrgRow[] {
    if (!allClients?.length) return [];
    if (isWerftoolsStaffRole(actor.role)) {
        return allClients.filter((c) => !c.parent_client_id);
    }
    const homeId = actor.clientId?.trim();
    if (!homeId) return [];
    const allowed = new Set<string>([homeId]);
    for (const c of allClients) {
        if (c.parent_client_id === homeId) allowed.add(c.id);
    }
    return allClients.filter((c) => allowed.has(c.id));
}

export function isOrganizationAllowedForActor(
    organizationId: string | null | undefined,
    actor: { role?: string | null; clientId?: string | null },
    allClients: ClientOrgRow[],
): boolean {
    if (!organizationId) return isWerftoolsStaffRole(actor.role);
    return filterOrganizationsForActor(allClients, actor).some((c) => c.id === organizationId);
}
